import { Link, useLocation } from 'react-router-dom';
import type { Me } from '../api';

interface TopBarProps {
  me: Me;
  onSignOut: () => void;
}

export function TopBar({ me, onSignOut }: TopBarProps) {
  const path = useLocation().pathname;
  const onAdmin = path === '/admin';

  return (
    <header className="sticky top-0 z-30 border-b hair-strong bg-ink/85 backdrop-blur-md">
      {/* Status ticker — mono editorial detail. Doubled for seamless loop. */}
      <div className="overflow-hidden border-b hair">
        <div className="marquee py-1.5 text-[10px] tracking-widestest text-cream-500">
          <TickerRow />
          <TickerRow />
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-5 md:px-10">
        <Link to="/" className="group flex items-center gap-4">
          <BrandMark />
          <div className="flex flex-col leading-none">
            <span className="font-display text-[19px] font-medium tracking-tightish text-cream">
              Surfboard
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
              Portal · Internal Issue
            </span>
          </div>
        </Link>

        <nav className="flex items-center gap-3 text-sm">
          {me.isAdmin && (
            <Link
              to={onAdmin ? '/' : '/admin'}
              className="group relative inline-flex items-center gap-2 rounded-none border-b-2 border-transparent px-2 py-1 font-mono text-[11px] uppercase tracking-widestest text-cream-500 transition hover:border-coral hover:text-cream"
            >
              {onAdmin ? (
                <>
                  <span aria-hidden>←</span>
                  Portal
                </>
              ) : (
                <>
                  Admin
                  <span aria-hidden className="text-coral">·</span>
                </>
              )}
            </Link>
          )}

          <div className="flex items-center gap-3 border-l hair pl-4">
            <div className="hidden flex-col items-end leading-none md:flex">
              <span className="text-[13px] font-medium text-cream">
                {me.name?.split(' ')[0] ?? me.email.split('@')[0]}
              </span>
              <span className="mt-1 font-mono text-[10px] uppercase tracking-widestest text-cream-500">
                {me.isAdmin ? 'Admin' : 'Member'}
              </span>
            </div>
            {me.avatarUrl ? (
              <img
                src={me.avatarUrl}
                alt={me.name ?? me.email}
                className="h-9 w-9 rounded-full ring-2 ring-coral/40"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="grid h-9 w-9 place-items-center rounded-full bg-cream-200 font-display text-sm text-cream ring-2 ring-coral/40">
                {(me.name ?? me.email).slice(0, 1).toUpperCase()}
              </span>
            )}
            <button
              onClick={onSignOut}
              className="ml-1 font-mono text-[10px] uppercase tracking-widestest text-cream-500 transition hover:text-coral"
              aria-label="Sign out"
              title="Sign out"
            >
              Exit
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
}

function TickerRow() {
  const items = [
    'PORTAL.SURFBOARD.CLOUD',
    'STATUS · ALL SYSTEMS ONLINE',
    'ISSUE №07',
    new Date().toISOString().slice(0, 10),
    'INTERNAL DISTRIBUTION',
    'CIRCULATION · TEAM',
    'SET IN FRAUNCES & INTER',
    'KEEP IT WEIRD',
  ];
  return (
    <div className="flex shrink-0 items-center gap-8 pr-8 font-mono">
      {items.map((t, i) => (
        <span key={i} className="flex items-center gap-3">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral animate-pulse-dot" />
          <span>{t}</span>
        </span>
      ))}
    </div>
  );
}

function BrandMark() {
  // A custom mark — abstracted surfboard / portal arch. Bold flat shape,
  // no gradient, no glassmorphism, no blur. Cream stroke + coral fill block.
  return (
    <span className="relative grid h-11 w-11 place-items-center rounded-sm bg-coral text-ink shadow-[0_8px_18px_-12px_rgba(255,90,60,0.8)]">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 2C8 2 5 6 5 11s3 11 7 11 7-6 7-11-3-9-7-9Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M12 2v20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}
